<template>
  <main class="site-shell">
    <div class="page-bg"></div>
    <div class="grid-bg"></div>

    <section id="home" class="hero section">
      <div class="container">
        <div v-reveal="{ delay: 100 }" class="eyebrow">
          <Sparkles :size="16" />
          Content Creator aus Langnau
        </div>

        <div class="hero__intro">
          <div>
            <h1 v-reveal="{ delay: 180, y: 36, duration: 900 }" class="hero__title">
               Momente, die bleiben.
            </h1>
          </div>

          <div v-reveal="{ delay: 320, y: 28, duration: 800 }" class="hero__copy">
            <p>
              Ich bin Krilin — ich fotografiere Sport, Teams und Events, die nicht gestellt wirken.
              Clean produziert, schnell geliefert und ready für Website, Social Media oder Club-Kommunikation.
            </p>

            <div class="hero__actions">
              <button
                type="button"
                class="button button--accent"
                @click="scrollToSection('work')"
              >
                Arbeiten ansehen
                <ArrowUpRight :size="18" />
              </button>

              <button
                type="button"
                class="button button--ghost"
                @click="scrollToSection('contact')"
              >
                Projekt starten
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="services" class="section">
      <div class="container">
        <SectionHeader label="Services" title="Mein Angebot">
          Kreative Leistungen für Clubs, Athleten und Events — reduziert auf das, was wirklich gebraucht wird.
        </SectionHeader>

        <div class="services-grid">
          <article
            v-for="(service, index) in services"
            :key="service.title"
            v-reveal="{ delay: index * 120 }"
            class="service-card"
          >
            <div class="service-card__visual">
              <component :is="service.icon" class="service-card__icon" :size="64" />
              <p>{{ service.kicker }}</p>
            </div>

            <div class="service-card__content">
              <div>
                <h3>{{ service.title }}</h3>
                <p>{{ service.text }}</p>
              </div>

              <ul>
                <li v-for="point in service.points" :key="point">
                  <span>
                    <Check :size="14" />
                  </span>
                  {{ point }}
                </li>
              </ul>
            </div>
          </article>
        </div>
      </div>
    </section>

        <section class="logo-trustbar">
      <div class="container logo-trustbar__inner">
        <p class="logo-trustbar__title">
          Selected clubs, companies & sports stories.
        </p>

        <div class="logo-trustbar__logos">
          <a
            v-for="brand in trustedBy"
            :key="brand.name"
            href="#work"
            class="logo-trustbar__item"
            :aria-label="brand.name"
            :title="brand.name"
          >
            <img
              :src="brand.logo"
              :alt="brand.name"
              class="logo-trustbar__logo"
            >
          </a>
        </div>
      </div>
    </section>

      <section id="work" class="section">
        <div class="container">
          <SectionHeader label="Work" title="Einblick in die Projekte">
            Eine wechselnde Auswahl aus meinen Sportbildern — jedes Bild wechselt in seinem eigenen Rhythmus.
          </SectionHeader>

          <div v-if="workImages.length" class="work-grid">
            <div
              v-for="slot in workSlots"
              :key="slot.id"
              class="work-grid__item"
              :class="`work-grid__item--${slot.size}`"
            >
              <transition name="work-fade" mode="out-in">
                <img
                  :key="workImages[slot.imageIndex]?.id"
                  :src="workImages[slot.imageIndex]?.image"
                  :alt="workImages[slot.imageIndex]?.title || 'Work image'"
                  class="work-grid__image"
                >
              </transition>

              <div class="work-grid__overlay"></div>

              <div class="work-grid__content">
                <h3>{{ workImages[slot.imageIndex]?.title }}</h3>
              </div>
            </div>
          </div>

          

          <div v-else class="work-empty">
            Keine Work-Bilder gefunden. Prüfe den Ordner:
            <strong>src/assets/images/work/</strong>
          </div>
        </div>
        <div class="work-actions">
          <a
            href="https://krilinschmid.mypixieset.com/"
            target="_blank"
            rel="noreferrer"
            class="button button--accent"
          >
            View full galleries
            <ArrowUpRight :size="18" />
          </a>
        </div>
      </section>
    <section class="section">
      <div class="container">
        <SectionHeader label="Philosophie" title="Mein Ansatz">
          Drei Prinzipien, die jede Produktion tragen.
        </SectionHeader>

        <div class="principles">
          <div
            v-for="(item, index) in principles"
            :key="item.nr"
            v-reveal="{ delay: index * 100 }"
            class="principle-row"
          >
            <p class="principle-row__nr">{{ item.nr }}</p>
            <h3>{{ item.title }}</h3>
            <p>{{ item.text }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="about" class="section about">
      <div class="container about__grid">
        <div v-reveal class="about-photo image-block image-block--tall">
          <img
            :src="portraitImage"
            alt="Portrait von Krilin"
            class="about-photo__image"
          >

          <div class="about-photo__overlay"></div>

          <div class="image-block__content">
            <p>About</p>
            <h3>Krilin Schmid</h3>
          </div>
        </div>

        <div v-reveal="{ delay: 160 }" class="about__content">
          <p class="section-kicker">About</p>
          <h2>Wer bin ich?</h2>

          <div class="about__text">
            <p>
              Ich bin Krilin, fotografiere vor allem Sport und mag Bilder, die direkt eine Geschichte erzählen —
              Zweikämpfe, Jubel, Fokus, Team-Vibes.
            </p>

            <p>
              Meine Website soll genau das widerspiegeln: clean, schnell, visuell stark und nicht überladen.
              Die warmen Grau- und Akzentfarben bleiben dabei der Signature-Look.
            </p>
          </div>

          <blockquote>
            <p>“Guter Content fühlt sich nicht nach Pflicht an. Er macht Lust, genauer hinzuschauen.”</p>
            <cite>— Krilin</cite>
          </blockquote>

          <button
            type="button"
            class="button button--accent about-more-button"
            @click="showAboutModal = true"
          >
            Mehr über mich
            <ArrowUpRight :size="18" />
          </button>
        </div>
      </div>
    </section>

    <section id="contact" class="section contact">
      <div class="container">
        <div v-reveal class="contact-card">
          <div>
            <p class="section-kicker">Kontakt</p>
            <h2>Ready für dein Projekt?</h2>

            <p>
              Schreib mir für Matchdays, Portraits, Club-Content oder eine Idee, die noch keinen Namen hat.
            </p>
          </div>

          <div class="contact-card__actions">
            <button
              type="button"
              class="button button--accent"
              @click="showContactModal = true"
            >
              <Mail :size="18" />
              Mail schreiben
            </button>

            <a
              href="https://www.instagram.com/krilin.schmid/"
              target="_blank"
              rel="noreferrer"
              class="button button--ghost"
            >
              <InstagramIcon />
              Instagram
            </a>
          </div>
        </div>

        <footer class="footer">
          <p>© 2026 Krilin · Sports Photography</p>
          <p>Born in Bern · built with warm grey energy</p>
        </footer>
      </div>
    </section>

    <AboutMeModal
      :open="showAboutModal"
      @close="showAboutModal = false"
    />
    <ContactModal
      :open="showContactModal"
      @close="showContactModal = false"
    />
  </main>
</template>

<script setup>
import { h, ref, onMounted, onUnmounted } from 'vue'
import {
  Camera,
  Video,
  Mail,
  Sparkles,
  ArrowUpRight,
  Check,
} from 'lucide-vue-next'

import portraitImage from '@/assets/images/KSC09560.webp'

import logoTigers from '@/assets/images/logos/unihockey_Tigers.png'
import logoLejon from '@/assets/images/logos/lejon.png'
import logoSCBern from '@/assets/images/logos/scb.png'
import logoKonolfingen from '@/assets/images/logos/sc_konolfingen_farbig.png'
import logoTurnverein from '@/assets/images/logos/logo_tvb.png'
import logoKensekai from '@/assets/images/logos/kensekai_karateschule.png'

import AboutMeModal from '@/components/AboutMeModal.vue'
import ContactModal from '@/components/ContactModal.vue'

const showAboutModal = ref(false)
const showContactModal = ref(false)

const workImageContext = require.context(
  '@/assets/images/work',
  false,
  /\.(png|jpg|jpeg|webp)$/
)

const workImages = workImageContext.keys().map((key) => {
  return {
    id: key,
    image: workImageContext(key)
  }
})

const workSlots = ref([
  { id: 1, imageIndex: 0, delay: 5000, size: 'large' },
  { id: 2, imageIndex: 1, delay: 5600, size: 'large' },
  { id: 3, imageIndex: 2, delay: 6200, size: 'large' },
  { id: 4, imageIndex: 3, delay: 5300, size: 'large' }
])

let workIntervals = []

function getRandomWorkIndex(currentIndex) {
  if (workImages.length <= 1) return 0

  let nextIndex = Math.floor(Math.random() * workImages.length)

  while (nextIndex === currentIndex) {
    nextIndex = Math.floor(Math.random() * workImages.length)
  }

  return nextIndex
}

function changeWorkSlot(slotIndex) {
  if (!workImages.length) return

  const currentIndex = workSlots.value[slotIndex].imageIndex

  workSlots.value[slotIndex] = {
    ...workSlots.value[slotIndex],
    imageIndex: getRandomWorkIndex(currentIndex),
  }
}

function scrollToSection(id) {
  const target = document.getElementById(id)

  if (!target) {
    return
  }

  const headerOffset = 110
  const targetPosition =
    target.getBoundingClientRect().top + window.pageYOffset - headerOffset

  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth',
  })
}

onMounted(() => {
  workIntervals = workSlots.value.map((slot, index) => {
    return window.setInterval(() => {
      changeWorkSlot(index)
    }, slot.delay)
  })
})

onUnmounted(() => {
  workIntervals.forEach((interval) => {
    window.clearInterval(interval)
  })

  workIntervals = []
})

const trustedBy = [
  {
    name: 'Unihockey Tigers',
    logo: logoTigers,
  },
  {
    name: 'Unihockey Lejon Zäziwil',
    logo: logoLejon,
  },
  {
    name: 'SC Bern',
    logo: logoSCBern,
  },
  {
    name: 'SC Konolfingen',
    logo: logoKonolfingen,
  },
  {
    name: 'Turnverein Bärau',
    logo: logoTurnverein,
  },
  {
    name: 'Kensekai Karateschule',
    logo: logoKensekai,
  },
]

const services = [
  {
    title: 'Photography',
    kicker: 'Echte Momente. Stark erzählt.',
    text: 'Fotografie für Portraits, Hochzeiten, Events, Sport, Street und Automotive — natürlich, atmosphärisch und mit einem klaren visuellen Stil.',
    points: ['Portraits', 'Weddings & Events', 'Sports & Automotive'],
    icon: Camera,
  },
  {
    title: 'Visual Content',
    kicker: 'Für Menschen, Marken & Geschichten.',
    text: 'Content für Social Media, Websites und Kampagnen — von einzelnen Bildern bis zu kompakten Foto- und Video-Serien.',
    points: ['Photo & Video', 'Social Media', 'Brand Stories'],
    icon: Video,
  },
]

const principles = [
  {
    nr: '01',
    title: 'Real Moments',
    text: 'Ich suche nach echten Augenblicken — nicht nach gestellten Bildern. Emotion, Nähe und Atmosphäre stehen im Mittelpunkt.',
  },
  {
    nr: '02',
    title: 'Consistent Look',
    text: 'Von der Auswahl bis zur Bearbeitung entsteht ein klarer visueller Stil, der sich hochwertig und wiedererkennbar anfühlt.',
  },
  {
    nr: '03',
    title: 'Made for Stories',
    text: 'Fotografie und Videografie für Menschen, Events, Hochzeiten, Sport, Street und Automotive — erzählt mit Gefühl und Charakter.',
  },
]

const InstagramIcon = {
  name: 'InstagramIcon',
  render() {
    return h(
      'svg',
      {
        width: 18,
        height: 18,
        viewBox: '0 0 24 24',
        fill: 'none',
        'aria-hidden': 'true',
      },
      [
        h('rect', {
          x: 3,
          y: 3,
          width: 18,
          height: 18,
          rx: 5,
          stroke: 'currentColor',
          'stroke-width': 2,
        }),
        h('circle', {
          cx: 12,
          cy: 12,
          r: 4,
          stroke: 'currentColor',
          'stroke-width': 2,
        }),
        h('circle', {
          cx: 17.5,
          cy: 6.5,
          r: 1.2,
          fill: 'currentColor',
        }),
      ]
    )
  },
}

const SectionHeader = {
  name: 'SectionHeader',
  props: {
    label: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },
  template: `
    <div class="section-header">
      <div>
        <p class="section-kicker">{{ label }}</p>
      </div>
      <div>
        <h2>{{ title }}</h2>
        <p v-if="$slots.default" class="section-header__text">
          <slot />
        </p>
      </div>
    </div>
  `,
}
</script>


<style scoped>
@font-face {
  font-family: 'Halenoir';
  src: url('/fonts/Halenoir.woff2') format('woff2');
  font-weight: 400 900;
  font-display: swap;
}

:global(html) {
  scroll-behavior: smooth;
}

:global(body) {
  margin: 0;
  background: #2f2e2e;
}

* {
  box-sizing: border-box;
}

.site-shell {
  --background: #2f2e2e;
  --secondary-background: #454545;
  --accent: #8b7054;
  --accent-secondary: #6c553d;
  --text-primary: #d6d1ce;
  --text-secondary: #e6e1de;

  position: relative;
  min-height: 100vh;
  overflow: hidden;
  background: var(--background);
  color: var(--text-primary);
  font-family: 'Halenoir', sans-serif;
}

.page-bg,
.grid-bg {
  position: fixed;
  inset: 0;
  pointer-events: none;
}

.page-bg {
  z-index: 0;
  background:
    radial-gradient(circle at 20% 0%, rgba(139, 112, 84, 0.24), transparent 34%),
    linear-gradient(180deg, #2f2e2e 0%, #373636 52%, #2f2e2e 100%);
}

.grid-bg {
  z-index: 0;
  opacity: 0.035;
  background-image:
    linear-gradient(to right, #d6d1ce 1px, transparent 1px),
    linear-gradient(to bottom, #d6d1ce 1px, transparent 1px);
  background-size: 80px 80px;
}

.container {
  position: relative;
  z-index: 1;
  width: min(100% - 40px, 1280px);
  margin-inline: auto;
}

.section {
  position: relative;
  z-index: 1;
  padding: 112px 0;
}

.nav {
  position: fixed;
  inset: 0 0 auto;
  z-index: 50;
  border-bottom: 1px solid rgba(214, 209, 206, 0.12);
  background: rgba(47, 46, 46, 0.86);
  backdrop-filter: blur(24px);
}

.nav__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: 20px;
}

.brand,
.nav__links,
.nav__link,
.button,
.mobile-menu__link {
  display: flex;
  align-items: center;
}

.brand {
  gap: 12px;
  color: var(--text-secondary);
  text-decoration: none;
}

.brand__logo {
  font-size: 1.25rem;
  font-weight: 900;
  letter-spacing: -0.05em;
}

.brand__dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: var(--accent);
}

.brand__subline {
  color: rgba(214, 209, 206, 0.62);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.25em;
  text-transform: uppercase;
}

.nav__links {
  gap: 32px;
}

.nav__link {
  gap: 8px;
  color: rgba(214, 209, 206, 0.82);
  font-size: 0.9rem;
  font-weight: 700;
  text-decoration: none;
  transition: color 180ms ease;
}

.nav__link span,
.mobile-menu__link span {
  color: var(--accent);
}

.nav__link:hover {
  color: var(--text-secondary);
}

.nav__toggle {
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 14px;
  background: rgba(230, 225, 222, 0.06);
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  padding: 10px 13px;
}

.mobile-menu {
  border-top: 1px solid rgba(214, 209, 206, 0.12);
  background: rgba(47, 46, 46, 0.96);
  padding: 14px 20px 18px;
}

.mobile-menu__link {
  gap: 12px;
  border-radius: 14px;
  color: var(--text-primary);
  font-weight: 700;
  padding: 14px 16px;
  text-decoration: none;
}

.mobile-menu__link:hover {
  background: rgba(230, 225, 222, 0.08);
}

.button {
  justify-content: center;
  gap: 10px;
  border-radius: 999px;
  border: 1px solid transparent;
  cursor: pointer;
  font-weight: 900;
  line-height: 1;
  min-height: 48px;
  padding: 0 24px;
  text-decoration: none;
  transition:
    transform 180ms ease,
    background 180ms ease,
    color 180ms ease,
    border-color 180ms ease;
}

.button:hover {
  transform: translateY(-2px);
}

.button--light {
  background: var(--text-secondary);
  color: var(--background);
}

.button--light:hover {
  background: var(--text-primary);
}

.button--accent {
  background: var(--accent);
  color: var(--background);
}

.button--accent:hover {
  background: var(--accent-secondary);
  color: var(--text-secondary);
}

.button--ghost {
  border-color: rgba(214, 209, 206, 0.16);
  background: rgba(230, 225, 222, 0.05);
  color: var(--text-secondary);
}

.button--ghost:hover {
  background: rgba(230, 225, 222, 0.1);
}

.hero {
  padding-top: 160px;
  padding-bottom: 80px;
}

.eyebrow,
.section-kicker {
  color: var(--accent);
  font-size: 0.82rem;
  font-weight: 900;
  letter-spacing: 0.28em;
  text-transform: uppercase;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(139, 112, 84, 0.32);
  border-radius: 999px;
  background: rgba(139, 112, 84, 0.12);
  color: var(--text-secondary);
  margin-bottom: 28px;
  padding: 10px 16px;
}

.hero__intro {
  display: grid;
  grid-template-columns: 1fr 0.78fr;
  align-items: end;
  gap: 48px;
}

.hero__title {
  max-width: 980px;
  color: var(--text-secondary);
  font-size: clamp(4rem, 9.5vw, 9.5rem);
  font-weight: 900;
  letter-spacing: -0.075em;
  line-height: 0.88;
  margin: 0;
}

.hero__copy p {
  color: rgba(214, 209, 206, 0.86);
  font-size: 1.2rem;
  line-height: 1.8;
  margin: 0;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 32px;
}

.hero__media-grid {
  display: grid;
  grid-template-columns: 1.35fr 0.65fr;
  gap: 20px;
  margin-top: 64px;
}

.hero__media-stack {
  display: grid;
  gap: 20px;
}

.image-block {
  position: relative;
  min-height: 320px;
  overflow: hidden;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 32px;
  background: var(--secondary-background);
  transition:
    transform 220ms ease,
    border-color 220ms ease,
    background 220ms ease;
}

.image-block:hover {
  transform: translateY(-6px);
  border-color: rgba(139, 112, 84, 0.45);
}

.image-block--tall {
  min-height: 520px;
}

.image-block__gradient,
.image-block__grid,
.image-block__content,
.image-block__badge {
  position: absolute;
}

.image-block__gradient {
  inset: 0;
  background:
    radial-gradient(circle at 30% 20%, rgba(139, 112, 84, 0.36), transparent 32%),
    linear-gradient(145deg, rgba(69, 69, 69, 0.35), rgba(47, 46, 46, 0.96));
}

.image-block__grid {
  inset: 0;
  opacity: 0.08;
  background-image:
    linear-gradient(to right, #d6d1ce 1px, transparent 1px),
    linear-gradient(to bottom, #d6d1ce 1px, transparent 1px);
  background-size: 48px 48px;
}

.image-block__content {
  inset: auto 0 0;
  padding: 24px;
}

.image-block__content p {
  color: var(--text-primary);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.28em;
  margin: 0 0 8px;
  text-transform: uppercase;
}

.image-block__content h3 {
  color: var(--text-secondary);
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: -0.04em;
  margin: 0;
}

.image-block__badge {
  top: 20px;
  right: 20px;
  border: 1px solid rgba(214, 209, 206, 0.16);
  border-radius: 999px;
  background: rgba(47, 46, 46, 0.34);
  color: var(--text-secondary);
  font-size: 0.75rem;
  font-weight: 800;
  padding: 9px 15px;
  backdrop-filter: blur(14px);
}

.trustbar {
  position: relative;
  z-index: 1;
  border-block: 1px solid rgba(214, 209, 206, 0.12);
  background: rgba(69, 69, 69, 0.36);
  padding: 28px 0;
}

.trustbar p {
  color: rgba(214, 209, 206, 0.48);
  font-size: 0.75rem;
  font-weight: 900;
  letter-spacing: 0.3em;
  margin: 0 0 20px;
  text-transform: uppercase;
}

.trustbar__items {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.trustbar__items span {
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 999px;
  background: rgba(69, 69, 69, 0.52);
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 800;
  padding: 10px 16px;
}

.section-header {
  display: grid;
  grid-template-columns: 0.35fr 1fr;
  align-items: end;
  gap: 24px;
  margin-bottom: 48px;
}

.section-header h2,
.about__content h2,
.contact-card h2 {
  color: var(--text-secondary);
  font-weight: 900;
  letter-spacing: -0.06em;
  line-height: 0.96;
  margin: 0;
}

.about-photo {
  position: relative;
  background: var(--secondary-background);
}

.about-photo__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.about-photo__overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      180deg,
      rgba(47, 46, 46, 0.08) 0%,
      rgba(47, 46, 46, 0.22) 45%,
      rgba(47, 46, 46, 0.72) 100%
    );
}

.section-header h2 {
  max-width: 820px;
  font-size: clamp(3rem, 5.2vw, 5rem);
}

.section-header__text {
  max-width: 680px;
  color: rgba(214, 209, 206, 0.86);
  font-size: 1.1rem;
  line-height: 1.75;
  margin: 20px 0 0;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.service-card {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  overflow: hidden;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 32px;
  background: rgba(69, 69, 69, 0.45);
  transition:
    transform 220ms ease,
    border-color 220ms ease,
    background 220ms ease;
}

.service-card:hover {
  transform: translateY(-6px);
  border-color: rgba(139, 112, 84, 0.45);
}

.service-card__visual,
.service-card__content {
  min-height: 480px;
  padding: 32px;
}

.service-card__visual {
  position: relative;
  overflow: hidden;
  background: var(--secondary-background);
}

.service-card__visual::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 35% 25%, rgba(139, 112, 84, 0.36), transparent 36%);
}

.service-card__icon,
.service-card__visual p {
  position: relative;
}

.service-card__icon {
  color: var(--text-primary);
}

.service-card__visual p {
  color: var(--text-primary);
  font-size: 0.86rem;
  font-weight: 900;
  letter-spacing: 0.28em;
  margin-top: 32px;
  text-transform: uppercase;
}

.service-card__content {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.service-card__content h3 {
  color: var(--text-secondary);
  font-size: 2.5rem;
  font-weight: 900;
  letter-spacing: -0.05em;
  line-height: 1;
  margin: 0;
}

.service-card__content p {
  color: rgba(214, 209, 206, 0.86);
  font-size: 1.05rem;
  line-height: 1.7;
  margin: 20px 0 0;
}

.service-card__content ul {
  display: grid;
  gap: 12px;
  list-style: none;
  margin: 40px 0 0;
  padding: 0;
}

.service-card__content li {
  display: flex;
  align-items: center;
  gap: 12px;
  color: var(--text-primary);
  font-size: 0.9rem;
  font-weight: 800;
}

.service-card__content li span {
  display: grid;
  width: 24px;
  height: 24px;
  place-items: center;
  border-radius: 999px;
  background: var(--accent);
  color: var(--background);
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
}

.principles {
  overflow: hidden;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 32px;
  background: rgba(69, 69, 69, 0.36);
}

.principle-row {
  display: grid;
  grid-template-columns: 0.18fr 0.42fr 1fr;
  gap: 20px;
  border-bottom: 1px solid rgba(214, 209, 206, 0.12);
  padding: 40px;
  transition: background 220ms ease;
}

.principle-row:hover {
  background: rgba(230, 225, 222, 0.04);
}

.principle-row:last-child {
  border-bottom: 0;
}

.principle-row__nr {
  color: var(--accent) !important;
  font-weight: 900;
}

.principle-row h3 {
  color: var(--text-secondary);
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1;
  margin: 0;
}

.principle-row p {
  color: rgba(214, 209, 206, 0.86);
  font-size: 1.05rem;
  line-height: 1.7;
  margin: 0;
}

.about__grid {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  align-items: center;
  gap: 40px;
}

.about__content h2 {
  font-size: clamp(3.5rem, 6vw, 7rem);
  margin-top: 16px;
}

.about__text {
  display: grid;
  gap: 20px;
  margin-top: 32px;
}

.about__text p {
  color: rgba(214, 209, 206, 0.86);
  font-size: 1.1rem;
  line-height: 1.8;
  margin: 0;
}

blockquote {
  border: 1px solid rgba(139, 112, 84, 0.34);
  border-radius: 32px;
  background: rgba(139, 112, 84, 0.12);
  margin: 40px 0 0;
  padding: 28px;
}

blockquote p {
  color: var(--text-secondary);
  font-size: 1.55rem;
  font-weight: 900;
  letter-spacing: -0.04em;
  line-height: 1.2;
  margin: 0;
}

blockquote cite {
  display: block;
  color: var(--text-primary);
  font-size: 0.82rem;
  font-style: normal;
  font-weight: 900;
  letter-spacing: 0.25em;
  margin-top: 18px;
  text-transform: uppercase;
}

.contact-card {
  display: grid;
  grid-template-columns: 1fr 0.55fr;
  align-items: end;
  gap: 40px;
  overflow: hidden;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 40px;
  background: rgba(69, 69, 69, 0.45);
  padding: 48px;
}

.contact-card h2 {
  font-size: clamp(4rem, 7vw, 8rem);
  margin-top: 16px;
}

.contact-card p:not(.section-kicker) {
  max-width: 680px;
  color: rgba(214, 209, 206, 0.86);
  font-size: 1.1rem;
  line-height: 1.7;
  margin: 28px 0 0;
}

.contact-card__actions {
  display: grid;
  gap: 12px;
}

.footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-top: 1px solid rgba(214, 209, 206, 0.12);
  color: rgba(214, 209, 206, 0.48);
  font-size: 0.9rem;
  margin-top: 40px;
  padding-top: 32px;
}

.mobile-only {
  display: none;
}

@media (max-width: 900px) {
  .desktop-only,
  .brand__dot,
  .brand__subline {
    display: none;
  }

  .mobile-only {
    display: inline-flex;
  }

  .section {
    padding: 88px 0;
  }

  .hero {
    padding-top: 128px;
  }

  .hero__intro,
  .hero__media-grid,
  .section-header,
  .services-grid,
  .service-card,
  .gallery-grid,
  .principle-row,
  .about__grid,
  .contact-card {
    grid-template-columns: 1fr;
  }

  .hero__title {
    font-size: clamp(4rem, 19vw, 6rem);
  }

  .hero__copy p {
    font-size: 1.05rem;
  }

  .service-card__visual,
  .service-card__content {
    min-height: unset;
  }

  .principle-row,
  .contact-card {
    padding: 28px;
  }

  .footer {
    align-items: flex-start;
    flex-direction: column;
  }
}

.logo-trustbar__logos {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: clamp(24px, 4vw, 64px);
}

.logo-trustbar__item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 96px;
  height: 64px;
  opacity: 0.62;
  filter: grayscale(1) brightness(1.25);
  transition:
    opacity 220ms ease,
    filter 220ms ease,
    transform 220ms ease;
}

.logo-trustbar__item:hover {
  opacity: 1;
  filter: grayscale(0) brightness(1);
  transform: translateY(-3px);
}

.logo-trustbar__logo {
  display: block;
  width: auto !important;
  height: auto !important;
  max-width: 86px !important;
  max-height: 46px !important;
  object-fit: contain;
}

@media (max-width: 900px) {
  .logo-trustbar__logos {
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 24px 34px;
  }

  .logo-trustbar__item {
    width: 84px;
    height: 58px;
  }

  .logo-trustbar__logo {
    max-width: 76px !important;
    max-height: 42px !important;
  }
}

.work-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  grid-auto-rows: 180px;
  gap: 18px;
}

.work-grid__item {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 28px;
  background: var(--secondary-background);
  transition:
    transform 220ms ease,
    border-color 220ms ease;
}

.work-grid__item:hover {
  transform: translateY(-5px);
  border-color: rgba(139, 112, 84, 0.55);
}

.work-grid__item--large {
  grid-column: span 3;
  grid-row: span 2;
}

.work-grid__item--wide {
  grid-column: span 3;
  grid-row: span 1;
}

.work-grid__item--small {
  grid-column: span 1;
  grid-row: span 1;
}

.work-grid__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.work-grid__overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  background:
    linear-gradient(
      180deg,
      rgba(47, 46, 46, 0.02) 0%,
      rgba(47, 46, 46, 0.18) 55%,
      rgba(47, 46, 46, 0.78) 100%
    );
}

.work-grid__content {
  position: absolute;
  left: 22px;
  right: 22px;
  bottom: 22px;
  z-index: 2;
}

.work-grid__content p {
  color: var(--text-primary);
  font-size: 0.7rem;
  font-weight: 900;
  letter-spacing: 0.26em;
  margin: 0 0 8px;
  text-transform: uppercase;
}

.work-grid__content h3 {
  color: var(--text-secondary);
  font-size: clamp(1.35rem, 2.4vw, 2.7rem);
  font-weight: 900;
  letter-spacing: -0.055em;
  line-height: 0.96;
  margin: 0;
}

.work-fade-enter-active,
.work-fade-leave-active {
  transition:
    opacity 520ms ease,
    transform 520ms ease;
}

.work-fade-enter-from {
  opacity: 0;
  transform: scale(1.04);
}

.work-fade-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.work-empty {
  border: 1px solid rgba(214, 209, 206, 0.12);
  border-radius: 24px;
  background: rgba(69, 69, 69, 0.36);
  color: var(--text-primary);
  padding: 32px;
}

.work-actions {
  display: flex;
  justify-content: center;
  margin-top: 34px;
}

.work-actions .button {
  min-height: 54px;
  padding-inline: 28px;
}

@media (max-width: 1000px) {
  .work-grid {
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 170px;
  }

  .work-grid__item--large,
  .work-grid__item--wide {
    grid-column: span 3;
  }

  .work-grid__item--small {
    grid-column: span 1;
  }
}

@media (max-width: 620px) {
  .work-grid {
    grid-template-columns: 1fr;
    grid-auto-rows: 260px;
  }

  .work-grid__item--large,
  .work-grid__item--wide,
  .work-grid__item--small {
    grid-column: span 1;
    grid-row: span 1;
  }
}

.about-more-button {
  margin-top: 28px;
}
</style>